/**
 * Offers Page (formerly "Proposals")
 *
 * Side-by-side offer compare is the hero. The page composes:
 *
 *   1. KPI strip (MetricGrid + MetricCell × 4)
 *   2. AstorOfferBanner — page-level explainer with inline bold numbers
 *   3. View toggle (Compare | List) in the page header right
 *   4. Either OffersCompareGrid (hero) OR OfferListView (flat list)
 *
 * Data shape: we merge the existing `useConsumerProposals` and
 * `useConsumerTermSheets` responses into a normalized `OfferRecord[]`. The
 * pricing fields come out of the term-sheet `calculatedTerms` JSON for now;
 * once 29-case-mgmt emits a first-class `OfferDto` the `mapTermSheetToOffer`
 * shim below can be deleted.
 *
 * Issues #47, #51, #1083 — Term Sheet viewing/acceptance + deal-linked sheets.
 *
 * @see /tmp/design-pass/borrower/specs/offers.md
 */

import CalendarIcon from '@mui/icons-material/CalendarToday';
import AcceptIcon from '@mui/icons-material/Check';
import RejectIcon from '@mui/icons-material/Close';
import ProposalIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import ExpandIcon from '@mui/icons-material/OpenInFull';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  EmptyState,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  MetricCell,
  MetricGrid,
  PageTabBar,
  PanelAttributeList,
  PanelSection,
  ShimmerLoader,
  SplitPanel,
  SplitPanelContent,
  SplitPanelFooter,
  SplitPanelHeader,
  Typography,
  useI18n,
  usePageTabs,
  useSplitPanel,
} from '@ops-matrix/design-system-ui';
import {
  type PageTabsConfig,
  ProposalAction,
  ProposalStatus,
  type TermSheetStatusType,
} from '@ops-matrix/shared-types';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import {
  AstorOfferBanner,
  bestMonthlySavings,
  OfferListView,
  type OfferRecord,
  type OfferStatus,
  OffersCompareGrid,
  pickWinnerId,
} from '../../components/offers';
import {
  useConsumerProposals,
  useConsumerTermSheets,
  useRespondToProposal,
  useRespondToTermSheet,
} from '../../lib/trpc/hooks';

// ============================================================================
// Helpers
// ============================================================================

const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return CURRENCY_FMT.format(value);
}

function formatDate(date: string | undefined | null): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDeadline(date: string | undefined | null): string | undefined {
  if (!date) return undefined;
  return new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function daysUntil(date: string | undefined | null): number | null {
  if (!date) return null;
  const deadline = new Date(date).getTime();
  const days = Math.ceil((deadline - Date.now()) / (1000 * 60 * 60 * 24));
  return Number.isNaN(days) ? null : days;
}

/** Issue #1083: Shared title construction for term sheets — kept exported for `ProposalDetailPage`. */
export function formatTermSheetTitle(
  borrower: { principal_name?: string } | null | undefined,
  termSheetNumber: string | undefined,
  termSheetLabel: string,
): string {
  if (borrower?.principal_name) return `${termSheetLabel} — ${borrower.principal_name}`;
  return `${termSheetLabel} ${termSheetNumber || ''}`.trim();
}

/** Maps a backend status string into the four-bucket OfferStatus surface. */
function normalizeStatus(raw: string | undefined): OfferStatus {
  if (raw === 'accepted' || raw === 'signed') return 'accepted';
  if (raw === 'rejected' || raw === 'declined') return 'rejected';
  if (raw === 'expired') return 'expired';
  return 'pending';
}

/** Lightweight projection from a Proposal row → OfferRecord. */
function mapProposalToOffer(p: Record<string, unknown>): OfferRecord {
  const id = (p.id as string) ?? '';
  return {
    id,
    shortId: `#${id.slice(0, 8)}`,
    lender: (p.lenderName as string) ?? (p.title as string) ?? '—',
    lenderTone: 'id',
    loanId: (p.caseId as string | undefined) ?? undefined,
    amount: (p.amount as number) ?? 0,
    rate: (p.rate as number) ?? 0,
    termMonths: (p.termMonths as number) ?? 0,
    monthly: (p.monthlyPayment as number) ?? 0,
    fees: (p.fees as number) ?? 0,
    status: normalizeStatus(p.status as string | undefined),
    deadline: formatDeadline(p.expiresAt as string | undefined),
    explain: (p.explain as string | undefined) ?? undefined,
  };
}

/** Term-sheet projection — pricing fields come out of `calculatedTerms`. */
function mapTermSheetToOffer(ts: Record<string, unknown>, lenderFallback: string): OfferRecord {
  const id = (ts.id as string) ?? '';
  const calc = (ts.calculatedTerms as Record<string, unknown> | undefined) ?? {};
  const borrower = ts.borrower as { principal_name?: string } | undefined;
  const lender = (ts.lenderName as string | undefined) ?? borrower?.principal_name ?? lenderFallback;

  return {
    id,
    shortId: ((ts.termSheetNumber as string | undefined) ?? `#${id.slice(0, 8)}`).toString(),
    lender,
    lenderTone: 'mint',
    loanId: (ts.caseId as string | undefined) ?? undefined,
    property: (ts.property as { address?: string } | undefined)?.address,
    amount: Number(calc.loan_amount) || 0,
    rate: Number(calc.interest_rate) || 0,
    termMonths: Number(calc.term_months) || 0,
    monthly: Number(calc.monthly_payment) || 0,
    fees: Number(calc.total_closing_costs) || 0,
    originationPercent: typeof calc.origination_rate === 'number' ? (calc.origination_rate as number) : undefined,
    originationDollars: typeof calc.origination_fee === 'number' ? (calc.origination_fee as number) : undefined,
    dscr: typeof calc.dscr === 'number' ? (calc.dscr as number) : undefined,
    ltvPercent: typeof calc.ltv === 'number' ? (calc.ltv as number) : undefined,
    status: normalizeStatus(ts.status as string | undefined),
    deadline: formatDeadline(ts.expiresAt as string | undefined),
    // TODO(backend-wiring): use the live `OfferDto.explain` string once 29-case-mgmt emits it.
    explain: undefined,
  };
}

// Tab configuration for URL-synced filtering — reused by the List view.
const OFFERS_TABS_CONFIG: PageTabsConfig = {
  tabs: [
    { id: 'all', label: 'common.all', path: '/proposals' },
    { id: 'pending', label: 'common.pending', path: '/proposals?tab=pending' },
    { id: 'accepted', label: 'common.accepted', path: '/proposals?tab=accepted' },
    { id: 'rejected', label: 'common.rejected', path: '/proposals?tab=rejected' },
  ],
  urlParam: 'tab',
  defaultTab: 'all',
};

type OffersView = 'compare' | 'list';

// ============================================================================
// Component
// ============================================================================

export default function ProposalsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  // First page only — KPI strip + compare grid don't paginate. List view
  // shows everything in the active tab via the existing tab filter.
  const page = 1;
  const pageSize = 10;

  // View toggle — defaults to Compare per spec §5.
  const [view, setView] = useState<OffersView>('compare');

  // URL-synced tabs (reused for List view filter)
  const { activeTab, setActiveTab, visibleTabs } = usePageTabs({
    config: OFFERS_TABS_CONFIG,
    searchParams,
    setSearchParams,
  });
  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
    },
    [setActiveTab],
  );

  const urlSync = useMemo(
    () => ({
      getPanelId: () => searchParams.get('panelId'),
      setPanelId: (id: string | null) => {
        const newParams = new URLSearchParams(searchParams);
        if (id) newParams.set('panelId', id);
        else newParams.delete('panelId');
        setSearchParams(newParams, { replace: true });
      },
    }),
    [searchParams, setSearchParams],
  );

  // Tab-driven status filters for the list view fetch.
  const statusFilter = useMemo((): ProposalStatus | undefined => {
    switch (activeTab) {
      case 'pending':
        return ProposalStatus.SENT;
      case 'accepted':
        return ProposalStatus.ACCEPTED;
      case 'rejected':
        return ProposalStatus.REJECTED;
      default:
        return undefined;
    }
  }, [activeTab]);

  const termSheetStatusFilter = useMemo((): TermSheetStatusType | undefined => {
    switch (activeTab) {
      case 'pending':
        return 'sent';
      case 'accepted':
        return 'accepted';
      case 'rejected':
        return 'rejected';
      default:
        return undefined;
    }
  }, [activeTab]);

  // Fetch sources
  const {
    data: proposalsResponse,
    isLoading: proposalsLoading,
    isError: proposalsError,
    error: proposalsErr,
  } = useConsumerProposals({ page, limit: pageSize, status: statusFilter });

  const {
    data: termSheetsResponse,
    isLoading: termSheetsLoading,
    isError: termSheetsError,
  } = useConsumerTermSheets({ page, limit: pageSize, status: termSheetStatusFilter });

  const isLoading = proposalsLoading || termSheetsLoading;
  const isError = proposalsError;
  const error = proposalsErr;
  const hasTermSheetError = termSheetsError && !termSheetsLoading;

  // Normalize both feeds into a single OfferRecord[]
  const offers: OfferRecord[] = useMemo(() => {
    const items: OfferRecord[] = [];
    const proposalSrc = proposalsResponse?.data as Record<string, unknown>[] | undefined;
    if (proposalSrc) {
      for (const p of proposalSrc) items.push(mapProposalToOffer(p));
    }
    const termSheetSrc = termSheetsResponse?.data as Record<string, unknown>[] | undefined;
    if (termSheetSrc) {
      for (const ts of termSheetSrc) items.push(mapTermSheetToOffer(ts, t('consumer.proposals.termSheetLabel')));
    }
    // Sort by deadline ascending — soonest decisions first.
    items.sort((a, b) => {
      const aTime = a.deadline ? new Date(a.deadline).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.deadline ? new Date(b.deadline).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
    return items;
  }, [proposalsResponse, termSheetsResponse, t]);

  // Pending-only set drives the compare grid; the list view shows everything.
  const pendingOffers = useMemo(() => offers.filter((o) => o.status === 'pending'), [offers]);
  const winnerId = useMemo(() => pickWinnerId(pendingOffers), [pendingOffers]);
  const winnerOffer = useMemo(() => pendingOffers.find((o) => o.id === winnerId), [pendingOffers, winnerId]);
  const savings = useMemo(() => bestMonthlySavings(pendingOffers), [pendingOffers]);

  // Soonest decision deadline (for KPI #4).
  const soonestDeadline = useMemo(() => {
    const stillPending = pendingOffers.filter((o) => o.deadline);
    if (stillPending.length === 0) return null;
    const sorted = [...stillPending].sort((a, b) => {
      const aTime = a.deadline ? new Date(a.deadline).getTime() : 0;
      const bTime = b.deadline ? new Date(b.deadline).getTime() : 0;
      return aTime - bTime;
    });
    return sorted[0]?.deadline ?? null;
  }, [pendingOffers]);
  const daysLeftToSoonest = daysUntil(soonestDeadline);

  // KPI values
  const totalOffers = offers.length;
  const pendingCount = pendingOffers.length;

  // SplitPanel state for the preview pane (preserves existing /proposals?panelId= URL contract)
  const findOfferById = useCallback((id: string) => offers.find((o) => o.id === id), [offers]);
  const { selectedItem, openPanel, closePanel, panelProps } = useSplitPanel<OfferRecord>({
    idField: 'id',
    urlSync,
    findItemById: findOfferById,
  });

  // Mutation wiring — accept/reject route through the same per-feed mutation
  // the previous version used, so the backend contract is unchanged.
  const respondProposal = useRespondToProposal();
  const respondTermSheet = useRespondToTermSheet();

  // Confirmation dialog state
  const [confirmOffer, setConfirmOffer] = useState<OfferRecord | null>(null);

  const handleAccept = useCallback((offer: OfferRecord) => {
    setConfirmOffer(offer);
  }, []);

  const isTermSheet = useCallback(
    (id: string) => {
      const tsSrc = termSheetsResponse?.data as Record<string, unknown>[] | undefined;
      return !!tsSrc?.some((ts) => ts.id === id);
    },
    [termSheetsResponse],
  );

  const handleConfirmAccept = useCallback(async () => {
    if (!confirmOffer) return;
    try {
      if (isTermSheet(confirmOffer.id)) {
        await respondTermSheet.mutateAsync({ termSheetId: confirmOffer.id, action: 'accept' });
      } else {
        await respondProposal.mutateAsync({ id: confirmOffer.id, action: ProposalAction.ACCEPT });
      }
      setConfirmOffer(null);
    } catch {
      // Mutation error is surfaced inline via `respondProposal.isError` /
      // `respondTermSheet.isError` selectors in the dialog footer.
      // TODO(backend-wiring): route this through `useToast()` once the consumer
      // portal mounts the design-system `ToastProvider`.
    }
  }, [confirmOffer, isTermSheet, respondProposal, respondTermSheet]);

  const handleCounter = useCallback(
    (offer: OfferRecord) => {
      // Counter flow reuses the existing detail page form (Collapse on the detail panel).
      const type = isTermSheet(offer.id) ? '&type=term_sheet' : '';
      navigate(`/proposals/${offer.id}?action=counter${type}`);
    },
    [isTermSheet, navigate],
  );

  const handleAsk = useCallback(
    (offer: OfferRecord) => {
      // TODO(backend-wiring): open Astor thread pre-seeded with `offer.id` once the
      // consumer-side Astor chat hook exposes a `seed(threadId, context)` action.
      navigate(`/proposals/${offer.id}?ask=astor`);
    },
    [navigate],
  );

  const handleView = useCallback(
    (offer: OfferRecord) => {
      const type = isTermSheet(offer.id) ? '?type=term_sheet' : '';
      navigate(`/proposals/${offer.id}${type}`);
    },
    [isTermSheet, navigate],
  );

  // Panel content for the split preview pane
  const panelContent = selectedItem ? (
    <>
      <SplitPanelHeader
        title={selectedItem.lender}
        subtitle={selectedItem.shortId}
        onClose={closePanel}
        actions={
          <GlassTooltip title={t('common.viewDetails')}>
            <GlassIconButton size="small" onClick={() => handleView(selectedItem)}>
              <ExpandIcon fontSize="small" />
            </GlassIconButton>
          </GlassTooltip>
        }
      />
      <SplitPanelContent>
        <PanelSection title={t('common.status')} icon={<InfoIcon />} collapsible={false}>
          <Box sx={{ mb: 2 }}>
            <GlassChip
              label={t(`status.${selectedItem.status}`)}
              size="small"
              color={selectedItem.status === 'pending' ? 'primary' : 'success'}
            />
          </Box>
        </PanelSection>
        <PanelSection title={t('consumer.proposals.timeline')} icon={<ScheduleIcon />} defaultExpanded>
          <PanelAttributeList
            attributes={[
              {
                label: t('consumer.offers.compare.amount'),
                value: formatCurrency(selectedItem.amount),
                icon: <CalendarIcon fontSize="small" />,
              },
              { label: t('consumer.offers.compare.monthly'), value: formatCurrency(selectedItem.monthly) },
              {
                label: t('consumer.proposals.expires'),
                value: selectedItem.deadline ? formatDate(selectedItem.deadline) : '—',
              },
            ]}
          />
        </PanelSection>
      </SplitPanelContent>
      <SplitPanelFooter>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {selectedItem.status === 'pending' ? (
            <>
              <GlassButton
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => navigate(`/proposals/${selectedItem.id}?action=reject`)}
                sx={{ flex: 1, minHeight: 44 }}
              >
                {t('common.reject')}
              </GlassButton>
              <GlassButton
                variant="glow"
                color="success"
                startIcon={<AcceptIcon />}
                onClick={() => handleAccept(selectedItem)}
                sx={{ flex: 1, minHeight: 44 }}
              >
                {t('consumer.offers.accept')}
              </GlassButton>
            </>
          ) : (
            <GlassButton
              variant="outlined"
              fullWidth
              startIcon={<ExpandIcon />}
              onClick={() => handleView(selectedItem)}
              sx={{ minHeight: 44 }}
            >
              {t('common.viewDetails')}
            </GlassButton>
          )}
        </Box>
      </SplitPanelFooter>
    </>
  ) : null;

  // View toggle (rendered into PageTemplate's `actions` slot)
  const viewToggle = (
    <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: 999, bgcolor: 'action.hover' }}>
      <GlassButton
        size="small"
        variant={view === 'compare' ? 'contained' : 'text'}
        color={view === 'compare' ? 'primary' : 'inherit'}
        startIcon={<ViewModuleIcon />}
        onClick={() => setView('compare')}
        sx={{ borderRadius: 999, minHeight: 36 }}
      >
        {t('consumer.offers.viewCompare')}
      </GlassButton>
      <GlassButton
        size="small"
        variant={view === 'list' ? 'contained' : 'text'}
        color={view === 'list' ? 'primary' : 'inherit'}
        startIcon={<ViewListIcon />}
        onClick={() => setView('list')}
        sx={{ borderRadius: 999, minHeight: 36 }}
      >
        {t('consumer.offers.viewList')}
      </GlassButton>
    </Box>
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <PageTemplate
      variant="list"
      titleKey="consumer.offers.pageTitle"
      description={t('consumer.offers.pageSubtitle')}
      icon={ProposalIcon}
      actions={viewToggle}
    >
      <Box sx={{ overflowX: 'hidden' }}>
        <SplitPanel
          panelOpen={panelProps.open}
          onPanelClose={closePanel}
          storageKey="consumer-offers-panel"
          panel={panelContent}
          panelAriaLabel={t('consumer.proposals.proposalDetails')}
          header={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
              {/* KPI strip */}
              <GlassCard sx={{ p: 2 }}>
                <MetricGrid columns={{ xs: 1, sm: 2, md: 4 }}>
                  <MetricCell label={t('consumer.offers.kpi.totalOffers')} value={totalOffers} />
                  <MetricCell
                    label={t('consumer.offers.kpi.pendingReview')}
                    value={pendingCount}
                    tone="warning"
                    helper={t('consumer.offers.kpi.pendingSub')}
                  />
                  <MetricCell
                    label={t('consumer.offers.kpi.bestSavings')}
                    value={savings ? `${formatCurrency(savings.savings)} /mo` : '—'}
                    tone="success"
                    helper={
                      savings ? t('consumer.offers.kpi.bestSavingsSub', { winner: savings.winnerLender }) : undefined
                    }
                  />
                  <MetricCell
                    label={t('consumer.offers.kpi.decideBy')}
                    value={soonestDeadline ?? '—'}
                    tone={daysLeftToSoonest !== null && daysLeftToSoonest <= 3 ? 'error' : 'inherit'}
                    helper={
                      daysLeftToSoonest !== null
                        ? t('consumer.offers.kpi.decideByDate', {
                            date: soonestDeadline ?? '',
                            daysLeft: daysLeftToSoonest,
                          })
                        : undefined
                    }
                  />
                </MetricGrid>
              </GlassCard>

              {/* Astor banner — page-level explainer */}
              {view === 'compare' && pendingOffers.length >= 2 && (
                <AstorOfferBanner
                  winnerLender={winnerOffer?.lender}
                  savingsFormatted={savings ? formatCurrency(savings.savings) : undefined}
                  breakEvenMonth={null}
                  confidencePercent={86}
                  tools={['pricing.compare', 'doc.read']}
                  onShowReasoning={() => {
                    // TODO(backend-wiring): emit a side panel with the trace from 19-control-tower.
                  }}
                />
              )}

              {hasTermSheetError && (
                <GlassAlert severity="warning" visible={true}>
                  {t('termsheets.loadFailed')}
                </GlassAlert>
              )}
            </Box>
          }
        >
          {isError ? (
            <GlassAlert severity="error" visible={true} sx={{ mb: 2 }}>
              Failed to load offers: {error?.message || 'Unknown error'}
            </GlassAlert>
          ) : isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <ShimmerLoader variant="rectangular" height={120} />
                </Box>
              ))}
            </Box>
          ) : view === 'compare' ? (
            pendingOffers.length >= 2 ? (
              <OffersCompareGrid
                offers={pendingOffers}
                winnerId={winnerId}
                onAccept={handleAccept}
                onCounter={handleCounter}
                onAsk={handleAsk}
              />
            ) : (
              <EmptyState
                icon={<ProposalIcon />}
                title={t('consumer.offers.compare.emptyState')}
                description={t('consumer.offers.noOffersDescription')}
              />
            )
          ) : (
            <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant="h6" gutterBottom>
                  {t('consumer.offers.list.title')}
                </Typography>
                <PageTabBar
                  tabs={visibleTabs}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  sticky={false}
                  size="small"
                />
              </Box>
              <OfferListView
                offers={offers}
                onView={(offer) => {
                  openPanel(offer);
                }}
                onAccept={handleAccept}
                onCounter={handleCounter}
                onAsk={handleAsk}
              />
            </GlassCard>
          )}
        </SplitPanel>
      </Box>

      {/* Accept confirmation dialog */}
      <Dialog open={confirmOffer !== null} onClose={() => setConfirmOffer(null)}>
        <DialogTitle>{t('consumer.offers.confirmAccept.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('consumer.offers.confirmAccept.body', { lender: confirmOffer?.lender ?? '' })}
          </DialogContentText>
          {(respondProposal.isError || respondTermSheet.isError) && (
            <GlassAlert severity="error" visible={true} sx={{ mt: 2 }}>
              {t('consumer.proposals.failedToRespond')}
            </GlassAlert>
          )}
        </DialogContent>
        <DialogActions>
          <GlassButton
            onClick={() => setConfirmOffer(null)}
            disabled={respondProposal.isPending || respondTermSheet.isPending}
          >
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            onClick={handleConfirmAccept}
            color="success"
            variant="glow"
            disabled={respondProposal.isPending || respondTermSheet.isPending}
          >
            {t('consumer.offers.confirmAccept.cta')}
          </GlassButton>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
}
